import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { PicklistValue } from '@/api/client';

/**
 * Dropdown of checkboxes over a numeric picklist (e.g. Stage, Status).
 * `selected` / `onChange` work in numbers; picklist values are coerced via Number().
 * `dot` gives an option its colour dot (status / priority / stage), as every Cosmos dropdown shows them —
 * a dot, never the full coloured pill.
 */
export function MultiSelect({
  label, options, selected, onChange, dot,
}: {
  label: string;
  options: PicklistValue[];
  selected: number[];
  onChange: (next: number[]) => void;
  dot?: (value: number, label: string) => string;
}) {
  const summary = useMemo(() => {
    if (selected.length === 0) return label;
    if (selected.length === 1) {
      const v = options.find((o) => Number(o.value) === selected[0]);
      return `${label}: ${v?.label ?? selected[0]}`;
    }
    return `${label}: ${selected.length}`;
  }, [label, options, selected]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm">{summary}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 overflow-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {options.map((o) => {
          const v = Number(o.value);
          return (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={selected.includes(v)}
              onCheckedChange={(c) => onChange(c ? [...selected, v] : selected.filter((x) => x !== v))}
              onSelect={(e) => e.preventDefault()}
            >
              {dot ? (
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: dot(v, o.label) }} />
                  {o.label}
                </span>
              ) : o.label}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
